/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { BaseFormService } from '../base';
import {
  CELL_FILTER,
  CELL_RENDERER,
  COMMON_ACTION_NAMES,
  CreateFormRules,
  ENTITY_TYPES,
  EntityId,
  FieldList,
  IApiListResponse,
  IBaseEntityType,
  IBaseFormService,
  IDocumentOperationOptions,
  IFilterQuery,
  IFormRules,
  IInventoryCount,
  IInventoryCountFormFieldPath,
  IInventoryCountGroup,
  IInventoryCountGroupFormFieldPath,
  IInventoryCountSelectedModalSites,
  IModalDialogOptions,
  ISite,
  ISiteInventoryCount,
  ISiteWithError,
  ISubTask,
  ITask,
  NotificationType,
  TASK_STATUS,
  WIDGET_COMPONENT,
  USER_CONFIG_MODULE_TYPE,
  USER_CONFIG_MODULE_NAME,
  GRID_LIST_VIEW_TYPE,
  IUserConfig,
  DID_STATUS_ACTION_MAPPING,
} from '../../types';
import { lazyInject } from '../../../containers/base/decorators';
import { ValidationService } from '../../../components/form';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  observableCombineLatest,
  observableThrowError,
  rx,
  Subscription,
} from '../../rx';
import { createFormRules as createInventoryCountGroupFormRules } from '@shared/services/inventory-count/inventory-count-group.form-rules';
import {
  BTN_PRIMARY_CLASS,
  REST_SITES_LEVEL_NAME,
  TIME_FORMATS,
  WIDGET_FAMILY,
} from '@config/global-constants';
import {
  CompanyUtilService,
  DmDataService,
  InventoryCountGroupService,
  InventoryCountService,
  NumberFormatService,
  STATE_KEYS,
  SitesService,
  TaskService,
} from '@shared/services';
import {
  differenceBy,
  eq,
  filter,
  flatten,
  forEach,
  get,
  set,
  head,
  includes,
  isEmpty,
  isEqual,
  isUndefined,
  map,
  merge,
  reduce,
  size,
  some,
  uniq,
  sortBy,
  chain,
  sum,
  isArray,
  find,
  toLower,
  keyBy,
  isNil,
  pick,
  has,
} from 'lodash';
import { BUTTON_FLEX_ORDER } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants';
import {
  BTN_ADD_SITES,
  BTN_CANCEL,
  BTN_OK,
  BTN_REMOVE,
  BTN_SAVE,
  BTN_YES,
  BUTTON_ACTIONS,
} from '@text-content/button-templates';
import {
  DROPDOWN_MENU_POSITION,
  ISiteTag,
  TOOLBAR_BUTTON_POSITION,
  XUX_GRID_ACTIONS,
  XuxGridAction,
} from '@geniuspos/ui-shared';
import {
  APIActions,
  ToolsActions,
  UiActions,
  XprtDataActions,
} from '@store/actions';
import {
  INVENTORY_COUNT_ITEM_STATUS_OPTIONS,
  INVENTORY_COUNT_STATUS_ENUM_OPTIONS,
  INVENTORY_COUNT_STATUS_VALUE,
} from '@app/shared/types/enums/inventory-count.enum';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { BehaviorSubject, Observable } from 'rxjs';
import { INVENTORY_COUNT_MODULE } from '@app/routes';
import { ActivatedRoute, Router } from '@angular/router';
import { PdfBaseService } from '@app/shared/common/make-pdf';
import { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { COLUMN_SELECTOR_ID, DID_KEYWORD_MAPPING } from '@shared/types';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import { setFormFieldValue } from '@components/form/services/schema-form/schema-form-functions';
import {
  generateObjectId,
  generateProgressBarLoadingParams,
  getSelectedItemsDescriptionText,
  toDate,
  toMoment,
} from '@shared/utils';
import {
  COMMON_WARN_ICON_COLUMN,
  COMMON_WARN_MESSAGE_CLASS,
} from '@shared/constants/common-warn-grid-setting';
import {
  ExportData,
  ExtraProp,
  SiteDataCommonRow,
  SiteDataForPdf,
  ExportOptions,
} from './inventory-count.type';
import { CHANGE_STATUS_TOTAL_STEPS } from './inventory-count.definition';
import {
  COMMON_FORM_PATH,
  NO_PERMISSION_STATUS,
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
  TOASTER_TRANSACTION_FAILED_MESSAGE,
} from '@app/shared/constants';
import { Metadata } from '@geniuspos/xenial-data-contracts/types/dm-entities';
import { XuxGridListComponent } from '@app/components/xux/xux-grid-list/xux-grid-list-widget.component';
import { DecimalNumber } from '@app/shared/common';
import { PdfGenerateServiceV2 } from '@app/shared/common/make-pdf/v2/pdf.generate.service';
import { InventoryCountPdfService } from './inventory-count-pdf.service';
import { buildInventoryCountPdfConfig } from './inventory-count-pdf-config';
import { IPdfData } from '@app/shared/common/make-pdf/v2/pdf-data.type';
import { InventoryItemVenueService } from '../inventory-item-venue';
import { InventoryCountGroupEditContainerComponent } from '@containers/inventory-counts/containers';
import { MODAL_TEMPLATES } from '@app/text-content';
import {
  dealInventoryCountPDFOptions,
  findColumnConfig,
} from './inventory-count.util';
import { SELECTABLE_COLUMNS } from './inventory-count.constants';
import { select } from '@angular-redux/store';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class InventoryCountGroupFormService
  extends BaseFormService
  implements IBaseFormService<ENTITY_TYPES.INVENTORY_COUNT_GROUP>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.INVENTORY_COUNT_GROUP;

  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(InventoryCountGroupService)
  public inventoryCountGroupService: InventoryCountGroupService;
  @lazyInject(InventoryCountService)
  public inventoryCountService: InventoryCountService;
  @lazyInject(CompanyUtilService) public companyUtilService: CompanyUtilService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(XprtDataActions) public xprtDataActions: XprtDataActions;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(ActivatedRoute) public route: ActivatedRoute;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(ToolsActions) public toolsActions: ToolsActions;
  @lazyInject(Router) public readonly router: Router;
  @lazyInject(PdfBaseService) public pdfBaseService: PdfBaseService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(InventoryCountPdfService)
  public inventoryCountPdfService: InventoryCountPdfService;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(TaskService) public taskService: TaskService;
  @select(STATE_KEYS.USER_CONFIG)
  public userConfig$: Observable<any>;

  public form: FormGroup;
  public createFormRulesFunction: CreateFormRules<InventoryCountGroupFormService> =
    createInventoryCountGroupFormRules;
  public siteListDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.instant(I18n.Common.CountsWithBrackets),
      }),
    );
  public disableAddSite$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isFinalStatus$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isSiteInventoryCountGridBusy$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public siteListTotal: number = 0;
  public siteListSelected: number = 0;
  public siteListSelectedItems: IInventoryCountFormFieldPath[] = [];
  public siteModalTotal: number = 0;
  public siteModalSelected: number = 0;
  public siteModalDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.instant(
          I18n.EntityNames.SiteSingularAndPlural,
        ),
      }),
    );
  public hiddenModifiedInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public companySitesList$: BehaviorSubject<ISite[]> = new BehaviorSubject<
    ISite[]
  >([]);
  public inventoryCountListGrid: XuxGridListComponent;
  public formFieldPath: IInventoryCountGroupFormFieldPath = {
    name: 'name',
    startDate: 'start_date',
    endDate: 'end_date',
    status: 'status',
    siteIds: 'site_ids',
    virtualSiteInventoryCounts: 'virtual.site_inventory_counts',
    virtualViewSetting: 'virtual.view_setting',
  };
  public sitesList: ISite[] = [];

  public get getViewSetting(): GRID_LIST_VIEW_TYPE {
    return this.stateService.getInventoryCountViewSetting();
  }

  public get userViewConfig(): IUserConfig {
    return this.stateService.getUserConfig(
      USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
      USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT,
    );
  }

  protected subscriptions: Subscription[] = [];

  public destroyFormDefinition(): void {
    this.isFinalStatus$.next(false);
    super.destroyFormDefinition();
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;
    this.initFetchSitesListSubscription();
    this.initVirtualSiteInventoryCountsSubscription();
    this.initVirtualViewSettingSubscription();

    const startDateControl = this.form.get(this.formFieldPath.startDate);
    if (this.isNewMode()) {
      const statusControl = this.form.get(this.formFieldPath.status);
      const startDate = this.companyUtilService.convertUTCDateWithTimeZone(
        toDate().toISOString(),
        true,
        TIME_FORMATS.L_LT,
      );
      startDateControl.setValue(startDate);
      statusControl.setValue(INVENTORY_COUNT_STATUS_VALUE.Open);
    } else {
      const startDate: string = this.getFormFieldValue(
        this.formFieldPath.startDate,
      );
      startDateControl.setValue(toMoment(startDate).format(TIME_FORMATS.L_LT));
    }

    let viewSetting = this.getViewSetting;
    if (!isEmpty(this.userViewConfig)) {
      viewSetting = get(
        this.userViewConfig,
        ['module_config', 'sheet_view_setting'],
        GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
      ) as GRID_LIST_VIEW_TYPE;
    }

    if (!isNil(this.inventoryCountService.viewSetting)) {
      viewSetting = this.inventoryCountService.viewSetting;
    }
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualViewSetting,
      viewSetting,
      false,
    );
    this.inventoryCountService.viewSetting = viewSetting;
    this.subscriptions.push(
      this.userConfig$.subscribe((allModuleColumnConfig) => {
        // For save column state
        this.allModuleColumnConfig = allModuleColumnConfig;
      }),
    );
    super.onFormReady(form);
  }

  public initFetchSitesListSubscription(): void {
    this.isSiteInventoryCountGridBusy$.next(true);
    this.subscriptions.push(this.refreshInventoryCounts().subscribe());
  }

  public refreshInventoryCounts(): Observable<any> {
    return observableCombineLatest([
      observableCombineLatest([
        this.sitesService.fetchList({
          filter: `is_active eq true`,
          select: ['name', 'id', 'store_number', 'metadata'],
          filter_by_assigned_sites: true,
        }),
        this.inventoryCountGroupService.fetchList(
          {
            filter: `is_active eq true and entity_id eq nin(${this.document?.entity_id || ''}) and status eq in(${
              INVENTORY_COUNT_STATUS_VALUE.Open
            }, ${INVENTORY_COUNT_STATUS_VALUE.Closed})`,
            select: ['entity_id', 'site_ids', 'status'],
          },
          {
            showErrorMessage: true,
          },
        ),
      ]).pipe(
        rx.map(([sitesResponse, inventoryCountGroupResponse]) => {
          const siteIdsInUse = uniq(
            flatten(map(inventoryCountGroupResponse.items, 'site_ids')),
          );
          const updatedSites = map(sitesResponse.items, (site) => ({
            ...site,
            siteInUse: siteIdsInUse.includes(site.id),
          })) as ISiteWithError[];
          this.hiddenModifiedInfo$.next(
            !some(updatedSites, (site) => site.siteInUse),
          );

          return updatedSites;
        }),
      ),
      this.fetchInventoryCounts({
        filter: `inventory_count_group_entity_id eq in(${this.document?.entity_id || ''})`,
        select: [
          'site_id',
          'status',
          'inventory_count_items',
          'cost',
          'variance_cost',
        ],
        include_calculated_values: true,
      }),
    ])
      .pipe(rx.finalize(() => this.isSiteInventoryCountGridBusy$.next(false)))
      .pipe(
        rx.map(([updatedSiteList, inventoryCountResponse]) => {
          this.prepareSiteList(updatedSiteList, inventoryCountResponse);
          this.setGrandTotalsValue(inventoryCountResponse);

          return [updatedSiteList, inventoryCountResponse];
        }),
      );
  }

  public fetchInventoryCounts(
    userQuery: IFilterQuery,
  ): Observable<IApiListResponse> {
    return this.inventoryCountService.fetchList(userQuery, {
      showErrorMessage: true,
    });
  }

  public prepareSiteList(
    sites: ISiteWithError[],
    inventoryCountResponse: IApiListResponse,
  ): void {
    this.sitesList = sites as ISite[];
    const inventoryCountList = inventoryCountResponse.items;
    const inventoryCountMap = keyBy(inventoryCountList, 'site_id');
    const siteIds = this.form.get(this.formFieldPath.siteIds).value;
    const inventoryCountSites = filter(
      this.sitesList,
      (site) =>
        siteIds?.includes(site.id) && !isEmpty(inventoryCountMap[site.id]),
    );
    const updatedCompanySites = inventoryCountSites.map((site: ISite) => {
      const currentCount = inventoryCountList.find(
        (count) => count.site_id === site.id,
      );

      return this.prepareSiteCountsGridItem(site, currentCount);
    });

    const sitesMap: { [field: string]: ISiteWithError } = keyBy(sites, 'id');
    const unSaveSites = filter(
      this.getFormFieldValue<IInventoryCountFormFieldPath[]>(
        this.formFieldPath.virtualSiteInventoryCounts,
      ),
      (item: IInventoryCountFormFieldPath) =>
        isEmpty(get(item, 'inventoryCountId')) &&
        !isEmpty(sitesMap[item.id]) &&
        !sitesMap[item.id].siteInUse,
    );
    updatedCompanySites.push(...unSaveSites);

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualSiteInventoryCounts,
      updatedCompanySites,
      false,
    );

    // This line of code is necessary, if you need to delete it, please be careful.
    const filteredCompanySites = differenceBy(
      this.sitesList,
      updatedCompanySites,
      'id',
    );
    this.companySitesList$.next(
      sortBy(filteredCompanySites, (site) => toLower(site?.name)),
    );

    this.isSiteInventoryCountGridBusy$.next(false);
  }

  public setGrandTotalsValue(inventoryCountResponse: IApiListResponse): void {
    const inventoryCounts: IInventoryCount[] = inventoryCountResponse.items;
    const totalCountedValue = this.getSubTotal(inventoryCounts, 'cost');
    const totalVarianceCost = this.getSubTotal(
      inventoryCountResponse.items,
      'variance_cost',
    );
    const totalExtendedValue = new DecimalNumber(totalCountedValue || 0)
      .sub(totalVarianceCost || 0)
      .valueOf();

    setFormFieldValue(
      this.form,
      'virtual.grand_totals',
      [
        {
          extended_value: totalExtendedValue || 0,
          counted_value: totalCountedValue || 0,
          variance_cost: totalVarianceCost || 0,
        },
      ],
      false,
    );
  }

  public getSubTotal(items: IInventoryCount[], targetPath: string): number {
    const hasInput = some(
      items,
      (item) => !isNaN(parseFloat(item?.[targetPath])),
    );
    if (!hasInput) return null;

    return reduce(
      items,
      (pre, cur) => {
        const curNumberStr = get(cur, targetPath, 0);
        const curNumber = parseFloat(`${curNumberStr}`);

        return pre.add(isNaN(curNumber) ? 0 : curNumberStr);
      },
      new DecimalNumber(0),
    ).valueOf();
  }

  public initVirtualSiteInventoryCountsSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.virtualSiteInventoryCounts)
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe((sites: ISite[]) => {
          const filteredCompanySites = differenceBy(
            this.sitesList,
            sites,
            'id',
          );
          this.companySitesList$.next(
            sortBy(filteredCompanySites, (site) => toLower(site?.name)),
          );
        }),
    );
  }

  public initVirtualViewSettingSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.virtualViewSetting).subscribe(
        async (view: GRID_LIST_VIEW_TYPE) => {
          setTimeout(() => {
            this.getFormFieldControl(
              this.formFieldPath.virtualViewSetting,
            ).markAsPristine();
            this.getFormFieldControl(
              this.formFieldPath.virtualViewSetting,
            ).markAsUntouched();
          }, 0);
          if (this.inventoryCountService.viewSetting === view) {
            return;
          }
          this.inventoryCountService.viewSetting = view;

          this.inventoryCountListGrid['updateActionButtons'](
            this.siteListSelectedItems,
          );
          const targetModuleConfig = findColumnConfig(
            this.allModuleColumnConfig,
            USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
          );
          if (isNil(targetModuleConfig)) {
            this.allModuleColumnConfig?.push({
              module_type: USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT,
              module_name: USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
              module_config: {
                ...get(this.userViewConfig, 'module_config', {}),
                sheet_view_setting: view,
              },
            });
          } else {
            targetModuleConfig.module_config = {
              ...get(this.userViewConfig, 'module_config', {}),
              sheet_view_setting: view,
            };
          }
          await this.toolsService
            .saveUserConfig(
              USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT,
              USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
              {
                ...get(this.userViewConfig, 'module_config', {}),
                sheet_view_setting: view,
              },
            )
            .toPromise();
        },
      ),
    );
  }

  public getSiteInventoryCountFormRules(
    formFieldPath: IInventoryCountGroupFormFieldPath,
    editor: InventoryCountGroupEditContainerComponent,
  ): IFormRules {
    const siteInventoryCountsFormFields: FieldList = {
      [formFieldPath.virtualSiteInventoryCounts]: {
        widget: WIDGET_COMPONENT.edit_grid,
        title: I18n.Common.SiteCounts,
        descriptionObs: this.siteListDescription$,
        suppressNoPermissionsDisable: true,
        widgetData: {
          useCustomButtonsOnFirstPosition: true,
          showLoader: this.isSiteInventoryCountGridBusy$,
          showSearch: true,
          multiselect: true,
          orderControls: false,
          disableColumnSelector: true,
          canRemove: true,
          showRowOptions: true,
          columns: [
            {
              title: I18n.Sites.SiteId,
              path: 'store_number',
              sortable: true,
              defaultView: true,
              filterParams: {
                quickFilter: true,
              },
              comparator: this.stringSortable(),
              cellRenderer: CELL_RENDERER.link_renderer,
              cellRendererParams: {
                onClick: (data): Promise<void> => this.openSiteCount([data]),
              },
            },
            {
              title: I18n.Sites.SiteName,
              path: 'name',
              sortable: true,
              defaultView: true,
              filterParams: {
                quickFilter: true,
              },
              comparator: this.stringSortable(),
              cellRenderer: CELL_RENDERER.link_renderer,
              cellRendererParams: {
                onClick: (data): Promise<void> => this.openSiteCount([data]),
              },
            },
            {
              title: I18n.Common.Status,
              path: 'status',
              sortable: true,
              defaultView: true,
              filterType: CELL_FILTER.enum,
              filterParams: {
                values: INVENTORY_COUNT_ITEM_STATUS_OPTIONS,
              },
              cellRendererParams: {
                valueToEnumMap: INVENTORY_COUNT_STATUS_ENUM_OPTIONS,
              },
              cellRenderer: CELL_RENDERER.prepared_string_renderer,
            },
            {
              title: I18n.Forms.InventoryCountForm.Variance,
              path: 'variance_cost',
              sortable: true,
              defaultView: true,
              valueFormatter: this.numberFormatService.getNumberFormatter(true),
            },
            {
              path: COLUMN_SELECTOR_ID,
              width: 50,
              title: '',
              defaultView: true,
              cellRenderer: CELL_RENDERER.site_inventory_count_actions_renderer,
              pinned: detectMobile() ? false : 'right',
              lockPinned: true,
            },
          ],
          userGridOptions: {
            overlayNoRowsTemplate: this.i18nService.instant(
              I18n.Common.NoRowsToShow,
            ),
          },
          buttons: [
            {
              ...BTN_ADD_SITES,
              flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
              disabledObs: this.disableAddSite$,
              hiddenObs: this.isFinalStatus$,
              position: TOOLBAR_BUTTON_POSITION.HEADER,
              dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
              click: this.openSiteSelector.bind(this),
            },
            {
              caption: I18n.Common.Actions,
              value: 'actions',
              flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
              hidden: isEmpty,
              childItems: [
                {
                  caption: I18n.Common.Edit,
                  value: 'Edit',
                  hidden: this.isEditButtonHidden.bind(this),
                  click: this.openSiteCount.bind(this),
                },
                {
                  caption: I18n.Common.Submit,
                  value: COMMON_ACTION_NAMES.Submit,
                  click: this.bulkSubmitClickHandler.bind(this),
                  hidden:
                    !editor.canManage || this.isSubmitButtonHidden.bind(this),
                },
                {
                  caption: I18n.Common.View,
                  value: 'View',
                  hidden: this.isViewButtonHidden.bind(this),
                  click: this.openSiteCount.bind(this),
                },
                {
                  value: 'separator',
                  position: TOOLBAR_BUTTON_POSITION.ROW,
                  hidden: (selectedItems): boolean =>
                    (this.isCloseCountButtonHidden(selectedItems) &&
                      this.isEditButtonHidden(selectedItems) &&
                      this.isViewButtonHidden(selectedItems) &&
                      (!editor.canManage ||
                        this.isSubmitButtonHidden(selectedItems))) ||
                    this.isCategoryExportHidden(
                      GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
                    ),
                },
                {
                  caption: I18n.Common.ExportByConsolidatedCategory,
                  value: 'consolidated category',
                  class: 'xux-action-button btn full-width',
                  hidden: this.isCategoryExportHidden.bind(
                    this,
                    GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
                  ),
                  click: (selectedSites): Promise<void> =>
                    this.bulkExportClick(selectedSites, {
                      mode: 'consolidate category',
                    }),
                },
                {
                  caption: I18n.Common.ExportByMajorCategory,
                  value: 'major category',
                  class: 'xux-action-button btn full-width',
                  hidden: this.isCategoryExportHidden.bind(
                    this,
                    GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
                  ),
                  click: (selectedSites): Promise<void> =>
                    this.bulkExportClick(selectedSites, {
                      mode: 'major category',
                    }),
                },
                {
                  caption: I18n.Common.Export,
                  value: 'alphabetical',
                  class: 'xux-action-button btn full-width',
                  hidden: this.isCategoryExportHidden.bind(
                    this,
                    GRID_LIST_VIEW_TYPE.ALPHABETICAL_LIST_VIEW,
                  ),
                  click: (selectedSites): Promise<void> =>
                    this.bulkExportClick(selectedSites, {
                      mode: 'alphabetical',
                    }),
                },
                {
                  value: 'separator',
                  position: TOOLBAR_BUTTON_POSITION.ROW,
                  hidden: this.isRemoveButtonHidden.bind(this),
                },
                {
                  ...BTN_REMOVE,
                  class: 'xux-action-button btn full-width',
                  hidden: this.isRemoveButtonHidden.bind(this),
                },
              ],
            },
            {
              caption: I18n.Common.Edit,
              click: this.editButtonClick.bind(this),
              hidden: this.isEditButtonHidden.bind(this),
              position: TOOLBAR_BUTTON_POSITION.ROW,
            },
            {
              caption: I18n.Common.Submit,
              click: this.submitClickHandler.bind(this),
              hidden: !editor.canManage || this.isSubmitButtonHidden.bind(this),
              position: TOOLBAR_BUTTON_POSITION.ROW,
            },
            {
              caption: I18n.Common.View,
              value: 'View',
              position: TOOLBAR_BUTTON_POSITION.ROW,
              hidden: this.isViewButtonHidden.bind(this),
              click: this.openSiteCount.bind(this),
            },
            {
              value: 'separator',
              position: TOOLBAR_BUTTON_POSITION.ROW,
              hidden: (selectedItems): boolean =>
                (this.isEditButtonHidden(selectedItems) &&
                  this.isViewButtonHidden(selectedItems) &&
                  (!editor.canManage ||
                    this.isSubmitButtonHidden(selectedItems))) ||
                this.isCategoryExportHidden(GRID_LIST_VIEW_TYPE.CATEGORY_VIEW),
            },
            {
              caption: I18n.Common.ExportByConsolidatedCategory,
              value: 'consolidated category',
              class: 'xux-action-button btn full-width',
              click: (selectedSites): Promise<void> =>
                this.bulkExportClick(selectedSites, {
                  mode: 'consolidate category',
                }),
              hidden: this.isCategoryExportHidden.bind(
                this,
                GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
              ),
              position: TOOLBAR_BUTTON_POSITION.ROW,
            },
            {
              caption: I18n.Common.ExportByMajorCategory,
              value: 'major category',
              class: 'xux-action-button btn full-width',
              click: (selectedSites): Promise<void> =>
                this.bulkExportClick(selectedSites, { mode: 'major category' }),
              hidden: this.isCategoryExportHidden.bind(
                this,
                GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
              ),
              position: TOOLBAR_BUTTON_POSITION.ROW,
            },
            {
              caption: I18n.Common.Export,
              class: 'xux-action-button btn full-width',
              value: 'alphabetical',
              hidden: this.isCategoryExportHidden.bind(
                this,
                GRID_LIST_VIEW_TYPE.ALPHABETICAL_LIST_VIEW,
              ),
              click: (selectedSites): Promise<void> =>
                this.bulkExportClick(selectedSites, { mode: 'alphabetical' }),
              position: TOOLBAR_BUTTON_POSITION.ROW,
            },
            {
              value: 'separator',
              position: TOOLBAR_BUTTON_POSITION.ROW,
              hidden: this.isRemoveButtonHidden.bind(this),
            },
            {
              ...BTN_REMOVE,
              position: TOOLBAR_BUTTON_POSITION.ROW,
              hidden: this.isRemoveButtonHidden.bind(this),
            },
          ],
          eventHandlers: {
            [XUX_GRID_ACTIONS.GRID_READY]: this.listSelectGridAction.bind(this),
            [XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED]:
              this.listSelectGridAction.bind(this),
            [XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED]:
              this.listSelectGridAction.bind(this),
            [XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED]:
              this.listSelectGridAction.bind(this),
            [XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED]:
              this.listSelectGridAction.bind(this),
          },
          composeConfirmationModalOptions:
            this.composeConfirmationModalOption.bind(this),
        },
        defaultValue: [],
      },
    };

    return {
      fields: siteInventoryCountsFormFields,
    };
  }

  public async openSiteSelector(): Promise<void> {
    const companySitesList: ISite[] = await this.companySitesList$
      .first()
      .toPromise();

    const entityIdsForMetadatas: EntityId[] = chain(companySitesList)
      .flatMap('metadata')
      .map('entity_id')
      .value();

    const siteSelectorGridSettings: IModalDialogOptions = {
      title: I18n.Common.SiteSelector,
      subTitle: this.siteModalDescription$,
      modalClass: `modal-lg`,
      buttons: [
        {
          ...BTN_OK,
          caption: this.getSiteSelectorButtonCaption.bind(this),
          class: 'pull-right btn-primary',
        },
        BTN_CANCEL,
      ],
      schema: {
        properties: {
          modified_message: { type: 'string' },
          items: { type: 'array', required: true },
        },
      },
      formRules: {
        fields: {
          modified_message: {
            widget: WIDGET_COMPONENT.label,
            widgetFamily: WIDGET_FAMILY.LEGACY,
            title: I18n.ErrorMessages.SiteInUseInventoryCountError,
            hiddenObs: this.hiddenModifiedInfo$,
            show: true,
            widgetData: {
              itemClass: COMMON_WARN_MESSAGE_CLASS,
            },
          },
          items: {
            widget: WIDGET_COMPONENT.site_selection_list,
            ignoreAutoTitleWhenTranslate: true,
            widgetData: {
              showHierarchyButton: false,
              valueField: 'id',
              treeViewMode: true,
              treeChildrenPropertyName: 'children',
              treeColumnTitle: 'hierarchy',
              defaultExpanded: false,
              ignoreItemValueOptimization: true,
              useVisibleItemCountDescription: true,
              searchPlaceholder: this.i18nService.instant(I18n.Common.Search),
              columns: [
                {
                  path: 'hierarchy',
                  title: I18n.Forms.InventoryCountForm.Group,
                  defaultView: true,
                  enableSorting: false,
                  minWidth: 170,
                  cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
                  hide: true,
                  showRowGroup: true,
                  cellClass: 'cell-hierarchy',
                  valueFormatter: (params: ValueFormatterParams): string => {
                    const hierarchyValue = get(params, 'data.hierarchy', '');

                    if (isEqual(hierarchyValue, REST_SITES_LEVEL_NAME)) {
                      return this.i18nService.instant(
                        I18n.Common.EverythingElse,
                      );
                    }

                    return hierarchyValue;
                  },
                },
                {
                  ...COMMON_WARN_ICON_COLUMN,
                  path: 'siteInUse',
                  cellRendererParams: {
                    iconClass: 'xvi-grid-warn-icon',
                    icon: 'icon-warning',
                  },
                  defaultView: true,
                },
                {
                  title: I18n.Sites.SiteId,
                  path: 'store_number',
                  defaultView: true,
                  sortable: true,
                  filterParams: {
                    quickFilter: true,
                  },
                  comparator: this.stringSortable(),
                },
                {
                  title: I18n.Sites.SiteName,
                  path: 'name',
                  defaultView: true,
                  sortable: true,
                  comparator: this.stringSortable(),
                  filterParams: {
                    quickFilter: true,
                  },
                },
                {
                  title: I18n.EntityNames.Tags,
                  path: 'metadata',
                  filterType: CELL_FILTER.entity,
                  filterParams: {
                    quickFilter: true,
                    entity: ENTITY_TYPES.TAG,
                    entitySelector: (item: ISiteTag): boolean =>
                      includes(entityIdsForMetadatas, item.entity_id),
                    default: {
                      type: 'containsAny',
                    },
                    filterOptions: ['containsAny'],
                    filterItemSourceQuery: {
                      filter: `metadata_type eq 'tag'`,
                      select: ['entity_id', 'name', '_id'],
                    },
                    filterValueGetter: (data): EntityId[] =>
                      flatten(map(data.metadata, 'entity_id')),
                  },
                  valueGetter: (params: ValueGetterParams): string => {
                    const metadata: Metadata[] = get(
                      params,
                      'data.metadata',
                      [],
                    );

                    return chain(metadata)
                      .filter({ metadata_type: 'tag' })
                      .map('name')
                      .join(', ')
                      .value();
                  },
                  getQuickFilterTextForTreeView: (
                    params: IBaseEntityType,
                  ): string => {
                    return params?.metadata
                      ? chain(params.metadata)
                          .filter({ metadata_type: 'tag' })
                          .map('name')
                          .join(', ')
                          .value()
                      : '';
                  },
                  defaultView: true,
                  sortable: true,
                },
              ],
              userGridOptions: {
                overlayNoRowsTemplate: this.i18nService.instant(
                  I18n.Common.NoRowsToShow,
                ),
                isRowSelectable: (row): boolean => !row.data?.siteInUse,
                rowClassRules: {
                  'data-warning': (row: any): boolean => row.data?.siteInUse,
                },
                deltaRowDataMode: false,
              },
              eventHandlers: {
                [XUX_GRID_ACTIONS.GRID_READY]:
                  this.listModalGridAction.bind(this),
                [XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED]:
                  this.listModalGridAction.bind(this),
                [XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED]:
                  this.listModalGridAction.bind(this),
                [XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED]:
                  this.listModalGridAction.bind(this),
                [XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED]:
                  this.listModalGridAction.bind(this),
              },
            },
          },
        },
      },
    };

    const modalOptions: IModalDialogOptions = merge(
      {},
      gridSelectModalOptions(this.companySitesList$, this.i18nService),
      defaultWidgetData,
      siteSelectorGridSettings,
    );

    const { confirmed, value } =
      await this.uiActions.formModalDialog(modalOptions);
    if (!confirmed) return;

    const sitesControl: AbstractControl = this.getFormFieldControl(
      this.formFieldPath.virtualSiteInventoryCounts,
    );
    const sites: ISite[] = this.getFormFieldValue<ISite[]>(
      this.formFieldPath.virtualSiteInventoryCounts,
    );
    const preparedSiteCountsGridItems: IInventoryCountFormFieldPath[] = map(
      value.items,
      (item: IInventoryCountFormFieldPath) =>
        this.prepareSiteCountsGridItem(item, {}),
    );

    this.setControlValue(sitesControl, [
      ...sites,
      ...preparedSiteCountsGridItems,
    ]);
  }

  public listModalGridAction(
    event: XuxGridAction,
    grid: XuxGridListComponent,
  ): void {
    switch (event.type) {
      case XUX_GRID_ACTIONS.GRID_READY:
        this.siteModalSelected = 0;
        this.siteModalTotal = event.payload.gridApi.getDisplayedRowCount();
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        this.siteModalTotal = event.payload?.rowData?.length || 0;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED:
        this.siteModalSelected = event.payload?.rowData?.length || 0;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        this.siteModalSelected = event.payload?.rowData?.length || 0;
        this.setSubtitleForModel(grid);
        break;
      case XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED:
        this.setSubtitleForModel(grid);
        break;
      default:
        return;
    }
  }

  public async bulkChangeStatus(
    inventoryCountIds: string[],
    toStatus: string,
    isSync: boolean,
    isSaveGroup = false,
  ): Promise<void> {
    try {
      const modalOptions: IModalDialogOptions = {
        title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
        text: this.i18nService.instant(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateHowManyTarget,
          {
            wantTo: this.i18nService.instant(I18n.Common.submit),
            count: isSync
              ? this.i18nService.instant(I18n.Common.the)
              : inventoryCountIds.length,
            target: isSync
              ? this.i18nService.instant(
                  I18n.Forms.InventoryCountForm.inventoryCount,
                )
              : this.i18nService.instant(
                  I18n.Forms.InventoryCountForm.inventorycounts,
                ),
          },
        ),
        buttons: [{ ...BTN_YES, class: BTN_PRIMARY_CLASS }, BTN_CANCEL],
      };
      const result = await this.uiActions.promptFormModal(modalOptions);
      if (!result.confirmed) return;
      this.uiActions.enableLoader(
        isSync
          ? null
          : generateProgressBarLoadingParams({ i18nService: this.i18nService }),
      );

      const sitesControl: AbstractControl = this.getFormFieldControl(
        this.formFieldPath.virtualSiteInventoryCounts,
      );
      const nameControl: AbstractControl = this.getFormFieldControl(
        this.formFieldPath.name,
      );
      if (isSaveGroup && nameControl?.dirty) {
        await this.inventoryCountGroupService
          .updateEntity(
            {
              [this.formFieldPath.name]: nameControl.value,
            },
            {
              endpoint: this.document?._id,
              noLoader: true,
              requiredToSendFields: [this.formFieldPath.name],
            },
          )
          .toPromise();

        nameControl.markAsPristine();
      }

      await this.taskService
        .getInventoryCountDmSyncResult(
          { inventoryCountIds },
          {
            isSync,
            noLoader: true,
            stepCount: CHANGE_STATUS_TOTAL_STEPS,
          },
        )
        .toPromise();
      const task = await this.taskService
        .getInventoryCountBulkChangeStatusResult(
          { inventoryCountIds, toStatus },
          {
            isSync,
            noLoader: true,
            currentStep: CHANGE_STATUS_TOTAL_STEPS,
            stepCount: CHANGE_STATUS_TOTAL_STEPS,
          },
        )
        .toPromise();

      const hasError = get(task, 'sub_tasks', []).some(
        (subTask: ISubTask) =>
          get(subTask, 'value.inventory_count_status') !== toStatus,
      );

      const updateMessageOpts = {
        did: DID_KEYWORD_MAPPING.submitted,
        wantTo: this.i18nService.instant(I18n.Common.submit),
        isSingle: isSync,
      };
      if (hasError) {
        // refresh this page
        this.shownUpdateMessage(updateMessageOpts, task);
        this.uiActions.disableLoader();

        nameControl.markAsPristine();
        sitesControl.markAsPristine();
        this.apiActions.openDocumentEditor(
          this.entityConfig.ENTITY_TYPE,
          this.document._id,
          { forceReload: true },
        );
      } else {
        // refresh data
        this.refreshInventoryCounts()
          .pipe(
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            rx.catchError((err, caught) => {
              this.shownUpdateMessage(updateMessageOpts, task);
              this.uiActions.disableLoader();

              return observableThrowError(err);
            }),
          )
          .subscribe(() => {
            this.shownUpdateMessage(updateMessageOpts, task);
            this.uiActions.disableLoader();
          });
      }
    } catch (e) {
      if (!isEqual(e?.status, NO_PERMISSION_STATUS)) {
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: e.title || TOASTER_ERROR_MESSAGE.title,
          body: e.meta?.message || TOASTER_TRANSACTION_FAILED_MESSAGE,
        });
      }
      this.uiActions.disableLoader();
    }
  }

  public shownUpdateMessage(
    msgOpts: {
      did: string;
      wantTo: string;
      isSingle: boolean;
    },
    task: ITask,
  ): void {
    const { did, wantTo, isSingle } = msgOpts;
    const target = this.i18nService.instant(
      I18n.Forms.InventoryCountForm.inventorycounts,
    );
    const upperCaseDid = this.i18nService.instant(
      get(DID_STATUS_ACTION_MAPPING[did], 'upperCase', ''),
    );
    const lowerCaseDid = this.i18nService.instant(
      get(DID_STATUS_ACTION_MAPPING[did], 'lowerCase', ''),
    );

    const subTasks: ISubTask[] = get(task, 'sub_tasks', []);
    const successCount = size(
      filter(
        subTasks,
        (subTask: ISubTask) => subTask.status === TASK_STATUS.completed,
      ),
    );
    const failedCount = size(
      filter(
        subTasks,
        (subTask: ISubTask) => subTask.status !== TASK_STATUS.completed,
      ),
    );

    if (failedCount === 0) {
      this.uiActions.showToasterMessage({
        type: NotificationType.SUCCESS,
        title: TOASTER_SUCCESS_MESSAGE.title,
        body: isSingle
          ? this.i18nService.instant(I18n.InfoMessages.Didsuccessfully, {
              did: upperCaseDid,
            })
          : this.i18nService.instant(
              I18n.InfoMessages.PartialOrAllsuccessfully,
              {
                target,
                did: lowerCaseDid,
                successCount,
                trailSymbol: this.i18nService.instant(
                  I18n.Common.ExclamationMark,
                ),
              },
            ),
      });
    } else {
      let errorMsg = this.i18nService.instant(
        I18n.ErrorMessages.BulkOperationFailed,
        {
          failedCount,
          target,
          did: lowerCaseDid,
        },
      );

      if (successCount > 0) {
        errorMsg =
          this.i18nService.instant(I18n.InfoMessages.PartialOrAllsuccessfully, {
            successCount,
            target,
            did: lowerCaseDid,
            trailSymbol: this.i18nService.instant(I18n.Common.PeriodWithSpace),
          }) + errorMsg;
      } else if (isSingle) {
        errorMsg = this.i18nService.instant(I18n.ErrorMessages.FailedToDo, {
          wantTo,
        });
      }

      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: errorMsg,
      });
    }
  }

  public async submitClickHandler(
    value: string,
    site: ISiteInventoryCount,
  ): Promise<void> {
    return this.bulkChangeStatus(
      [site.inventoryCountId],
      INVENTORY_COUNT_STATUS_VALUE.Closed,
      true,
    );
  }

  public async bulkSubmitClickHandler(
    selectedSites: ISiteInventoryCount[],
  ): Promise<void> {
    const inventoryCountIds = map(selectedSites, 'inventoryCountId');

    return this.bulkChangeStatus(
      inventoryCountIds,
      INVENTORY_COUNT_STATUS_VALUE.Closed,
      inventoryCountIds.length === 1,
    );
  }

  public async openSiteCount(
    selectedSites?: ISiteInventoryCount[],
    preventRouteRedirect = false,
  ): Promise<void> {
    const site = head(selectedSites);
    const doc = get(this.form, 'value');
    const inventoryCountGroup = this.document;
    const siteIds = map(
      get(doc, this.formFieldPath.virtualSiteInventoryCounts, []),
      'id',
    );
    const preparedDoc = { ...doc, site_ids: siteIds };

    if (this.form?.dirty) {
      const modalOptions: IModalDialogOptions = {
        title: I18n.Forms.InventoryCountForm.SaveCount,
        text: I18n.ConfirmMessages.SaveInventoryCountConfirmationMessage,
        buttons: [BTN_SAVE, BTN_CANCEL],
      };
      const result = await this.uiActions.promptFormModal(modalOptions);

      if (!result.confirmed) {
        this.form.markAsDirty();

        return;
      }

      if (!this.form?.valid) {
        const modalOptionsInvalidForm: IModalDialogOptions = {
          title: I18n.Forms.InventoryCountForm.SaveCount,
          text: this.i18nService.instant(
            I18n.Forms.InventoryCountForm
              .YourInventoryCountDetailsFormHasErrors,
          ),
          buttons: [BTN_OK],
        };
        await this.uiActions.promptFormModal(modalOptionsInvalidForm);

        return;
      }

      const createdDoc = await this.saveInventoryCountGroup(preparedDoc);
      if (createdDoc?.errors) {
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: createdDoc?.errors.title || TOASTER_ERROR_MESSAGE.title,
          body:
            createdDoc?.errors?.detail?.detail ||
            createdDoc?.errors?.meta?.message ||
            TOASTER_TRANSACTION_FAILED_MESSAGE,
        });

        return;
      }

      if (this.isNewMode()) this.document = createdDoc;
      const createdCountsResponse = await this.updateSiteInventoryCounts(
        preparedDoc.entity_id,
      );
      if (some(createdCountsResponse, (count) => !isEmpty(count.errors))) {
        return;
      }
      const siteInventoryCounts = this.getFormFieldValue<
        IInventoryCountFormFieldPath[]
      >(this.formFieldPath.virtualSiteInventoryCounts);

      const newSiteIds = map(
        filter(siteInventoryCounts, (row) => isEmpty(row.inventoryCountId)),
        'id',
      );
      let newSiteCounts;
      if (!isEmpty(newSiteIds)) {
        const { items: siteCounts } = await this.fetchInventoryCounts({
          filter: `inventory_count_group_entity_id eq in(${preparedDoc.entity_id}) and site_id eq in(${newSiteIds.join()})`,
          select: [
            'site_id',
            'status',
            'inventory_count_items',
            'cost',
            'variance_cost',
          ],
          include_calculated_values: true,
        }).toPromise();

        // Assign the inventory count id to the new sites
        forEach(selectedSites, (selectedSite) => {
          if (includes(newSiteIds, selectedSite.id)) {
            selectedSite.inventoryCountId = get(
              find(siteCounts, { site_id: selectedSite.id }),
              '_id',
              '',
            );
          }
        });

        newSiteCounts = siteCounts;
      }

      this.form.markAsPristine();
      if (preventRouteRedirect) {
        if (!this.isNewMode()) {
          // If do not redirect to a new page, should maintain virtualSiteInventoryCounts,name and site_ids
          forEach(siteInventoryCounts, (siteCount) => {
            if (isEmpty(siteCount.inventoryCountId)) {
              siteCount.inventoryCountId = get(
                find(newSiteCounts, { site_id: siteCount.id }),
                '_id',
                '',
              );

              set(
                siteCount,
                'variance_cost',
                get(
                  find(newSiteCounts, { site_id: siteCount.id }),
                  'variance_cost',
                  0,
                ),
              );
            }
          });
          setFormFieldValue(
            this.form,
            this.formFieldPath.virtualSiteInventoryCounts,
            siteInventoryCounts,
            false,
          );

          const allSiteIds = map(siteInventoryCounts, 'id');
          setFormFieldValue(
            this.form,
            this.formFieldPath.siteIds,
            allSiteIds,
            false,
          );
          this.document.site_ids = allSiteIds;

          // Update the inventory count group name into this.document.
          this.document.name = this.getFormFieldValue<
            IInventoryCountFormFieldPath[]
          >(this.formFieldPath.name);
        }

        return;
      }
      const routeUri = isEmpty(site.inventoryCountId)
        ? `${INVENTORY_COUNT_MODULE}/add`
        : `${INVENTORY_COUNT_MODULE}/${site.inventoryCountId}`;
      const docToStore = {
        ...preparedDoc,
        _id: createdDoc?._id || inventoryCountGroup?._id,
      };
      this.uiActions.openRoute(routeUri, {
        forceReload: false,
        state: { site, inventoryCountGroup: docToStore },
      });

      return;
    }

    if (preventRouteRedirect) return;

    this.uiActions.openRoute(
      `${INVENTORY_COUNT_MODULE}/${site?.inventoryCountId}`,
      {
        forceReload: false,
        state: { inventoryCountGroup, site },
      },
    );
  }

  public async bulkExportClick(
    selectedSites: ISiteInventoryCount[],
    options: ExportOptions,
  ): Promise<void> {
    if (!isArray(selectedSites) || isEmpty(selectedSites)) {
      return;
    }

    if (this.form?.dirty) {
      await this.openSiteCount(selectedSites, true);
      // If the unsaved change still exist, just stop the process
      if (this.form?.dirty) return;
    }

    const siteIds = map(selectedSites, 'id');
    const inventoryCountIds = map(selectedSites, 'inventoryCountId');

    const onlyUseXVIData = some(selectedSites, {
      status: INVENTORY_COUNT_STATUS_VALUE.Completed,
    });

    try {
      const isSync = size(selectedSites) === 1;

      this.uiActions.enableLoader(
        isSync || onlyUseXVIData
          ? null
          : generateProgressBarLoadingParams({ i18nService: this.i18nService }),
      );

      if (!onlyUseXVIData) {
        await this.taskService
          .getInventoryCountDmSyncResult(
            { inventoryCountIds },
            {
              isSync,
              noLoader: true,
            },
          )
          .toPromise();
      }

      await this.exportPdf(siteIds, {
        onlyUseXVIData,
        mode: options.mode,
        columnsHideIds: SELECTABLE_COLUMNS,
        noLoader: true,
      });
      if (this.isNewMode()) {
        // When the mode is new, direct to the editor page
        this.apiActions.openDocumentEditor(
          this.entityConfig.ENTITY_TYPE,
          this.document._id,
          {
            forceReload: true,
          },
        );
      }
    } finally {
      this.uiActions.disableLoader();
    }
  }

  public async exportPdf(
    siteIds: string[],
    options: ExportOptions & {
      onlyUseXVIData: boolean;
      noLoader?: boolean;
    },
  ): Promise<void> {
    if (isEmpty(siteIds)) {
      return;
    }

    const query = {
      select: [
        'code',
        'reporting_category_major',
        'reporting_category_minor',
        'name',
        'site_id',
        InventoryCountGroupService.isCompanyLevel
          ? 'measures'
          : 'physical_count_measures',
      ],
      filter: InventoryCountGroupService.isCompanyLevel
        ? 'measures/transactions/physical_count eq in(true)'
        : 'physical_count_measures/factor_value gt 0',
      ...(InventoryCountGroupService.isCompanyLevel
        ? {
            masterFilter: 'measures/transactions/physical_count eq in(true)',
          }
        : {}),
    };

    let dataWithCountsRes: IApiListResponse<IBaseEntityType>;
    let inventoryItemsRes: IBaseEntityType;
    //if is alphabetical && show or hide major or minor should adjust column width
    const newOptions = dealInventoryCountPDFOptions(
      get(
        this.stateService.getUserConfig(
          USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
          USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT,
        ),
        'module_config.alphabetical_list_view',
        [],
      ),
      options,
    );
    const pdfConfig = buildInventoryCountPdfConfig(
      async (_, bulkDoc: IBaseEntityType) => {
        const siteId = bulkDoc.site_id;
        const siteInfo = find(this.sitesList, (site) =>
          isEqual(site.id, siteId),
        );

        const pdfItems = await this.getPdfDataItems(bulkDoc.site_id, {
          dataWithCountsRes,
          inventoryItemsRes,
          onlyUseXVIData: options.onlyUseXVIData,
        });

        return this.inventoryCountPdfService.getPdfData(
          {
            name: this.getFormFieldValue('name'),
            site_info: siteInfo,
            site_id: isEmpty(siteInfo) ? siteId : null,
            inventory_count_items: pdfItems,
          },
          {
            isFromDetail: false,
            mode: options.mode,
          },
        );
      },
      {
        ...pick(newOptions, ['mode', 'columnsHideIds']),
        bulkOptions: {
          sortFiledPath: 'siteInfo.name',
          data: async (): Promise<IPdfData[]> => {
            const dataWithCountsResPromise =
              this.getInventoryCountWithSiteData(siteIds).toPromise();
            const inventoryItemsResPromise = !options.onlyUseXVIData
              ? this.dmDataService
                  .getSiteGroupIdList(siteIds)
                  .pipe(
                    rx.mergeMap((groupIds: string[]) =>
                      this.inventoryItemVenueService.batchFetchCurrentList(
                        groupIds,
                        query,
                      ),
                    ),
                  )
                  .toPromise()
              : Promise.resolve({});

            [dataWithCountsRes, inventoryItemsRes] = await Promise.all([
              dataWithCountsResPromise,
              inventoryItemsResPromise,
            ]);

            return map(siteIds, (id) => ({
              site_id: id,
            }));
          },
        },
        i18nService: this.i18nService,
      },
      { isFromDetail: false },
    );

    if (has(options, 'noLoader')) {
      set(pdfConfig, 'options.isShownLoadingWhenGenerate', !options.noLoader);
    }
    await this.pdfGenerateServiceV2.downloadPdfAsync(pdfConfig);
  }

  public async getPdfDataItems(
    siteId: string,
    additionalInfo: {
      onlyUseXVIData: boolean;
      dataWithCountsRes: IApiListResponse<IBaseEntityType>;
      inventoryItemsRes: IBaseEntityType;
    },
  ): Promise<IBaseEntityType> {
    const defaultPdfItems = [];
    const { onlyUseXVIData, dataWithCountsRes, inventoryItemsRes } =
      additionalInfo;

    const curSiteCounts = find(dataWithCountsRes.items, (countData) =>
      isEqual(siteId, countData.site_id),
    );

    if (
      onlyUseXVIData &&
      isEmpty(curSiteCounts?.virtual?.inventory_count_items)
    ) {
      return defaultPdfItems;
    }

    const curSiteInventoryItems = filter(inventoryItemsRes?.items ?? [], {
      site_id: siteId,
    });

    if (!onlyUseXVIData && isEmpty(curSiteInventoryItems)) {
      return defaultPdfItems;
    }

    const formatDataFromXVI = this.formatSiteDataForExport(
      get(curSiteCounts, ['virtual', 'inventory_count_items'], []),
      true,
      {
        id: [siteId],
      },
    );

    let pdfData: SiteDataForPdf | IBaseEntityType = formatDataFromXVI;

    if (!onlyUseXVIData) {
      const formatDataFromDM = this.formatSiteDataForExport(
        curSiteInventoryItems,
        false,
        { id: [siteId] },
      );

      pdfData = this.updateXVICountsOnDMApiData(
        formatDataFromDM,
        formatDataFromXVI,
      );
    }

    return pdfData?.items ?? defaultPdfItems;
  }

  public inventoryCountNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.form) return null;
      const controlValue = control.value;

      return !isEmpty(controlValue)
        ? null
        : {
            error: this.i18nService.instant(I18n.Common.EnterANameForEntity, {
              entityName: this.i18nService.instant(
                I18n.EntityNames.InventoryCount,
              ),
            }),
          };
    };
  }

  public listSelectGridAction(
    event: XuxGridAction,
    grid: XuxGridListComponent,
  ): void {
    switch (event.type) {
      case XUX_GRID_ACTIONS.GRID_READY:
        this.inventoryCountListGrid = grid;
        this.siteListSelected = 0;
        this.siteListSelectedItems = [];
        this.siteListTotal = event.payload.gridApi.getDisplayedRowCount();
        this.siteListDescription$.next(
          this.getSitesListText(
            this.siteListSelected,
            this.siteListTotal,
            I18n.Common.CountsWithBrackets,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED:
        this.siteListTotal = event.payload?.rowData?.length || 0;
        this.siteListDescription$.next(
          this.getSitesListText(
            this.siteListSelected,
            this.siteListTotal,
            I18n.Common.CountsWithBrackets,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED:
        this.siteListSelectedItems = event.payload?.rowData ?? [];
        this.siteListSelected = event.payload?.rowData?.length || 0;
        this.siteListDescription$.next(
          this.getSitesListText(
            this.siteListSelected,
            this.siteListTotal,
            I18n.Common.CountsWithBrackets,
          ),
        );
        break;
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        this.subscriptions.push(
          grid.visibleItemCount$.subscribe((totalSiteVisible: number) => {
            this.siteListTotal = totalSiteVisible;
            this.siteListDescription$.next(
              this.getSitesListText(
                this.siteListSelected,
                this.siteListTotal,
                I18n.Common.CountsWithBrackets,
              ),
            );
          }),
        );
        break;
      case XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED:
        const selectedSite = head(event.payload.rowData as ISite[]);
        this.openSiteCount([selectedSite]);
        break;
      default:
        return;
    }
  }

  public getSitesListText(
    siteListSelected: number,
    siteListTotal: number,
    entity: string,
  ): string {
    return getSelectedItemsDescriptionText(siteListSelected, siteListTotal, {
      entities: entity,
      i18nService: this.i18nService,
    });
  }

  public isCloseCountButtonHidden(
    selectedItems: ISiteInventoryCount[],
  ): boolean {
    if (selectedItems.length > 0) {
      let isAnySiteHaveNoCount: boolean = false;
      forEach(selectedItems, (site) => {
        if (isEmpty(site.inventoryCounts)) {
          isAnySiteHaveNoCount = true;

          return false;
        }
        const totalCount = sum(map(site.inventoryCounts, 'count'));
        if (totalCount === 0) {
          isAnySiteHaveNoCount = true;

          return false;
        }
      });
      if (isAnySiteHaveNoCount) return true;
    }

    return some(
      selectedItems,
      (item) =>
        isEqual(item.status, INVENTORY_COUNT_STATUS_VALUE.Closed) ||
        isEqual(item.status, INVENTORY_COUNT_STATUS_VALUE.Completed) ||
        isEmpty(item.inventoryCountId),
    );
  }

  public isCompletedCountGroup(): boolean {
    const inventoryCountGroupStatus = this.getFormFieldValue<string>(
      this.formFieldPath.status,
    );

    return inventoryCountGroupStatus === INVENTORY_COUNT_STATUS_VALUE.Completed;
  }

  public isRemoveButtonHidden(selectedItems: ISiteInventoryCount[]): boolean {
    return (
      this.isCompletedCountGroup() ||
      some(selectedItems, (item) =>
        isEqual(item.status, INVENTORY_COUNT_STATUS_VALUE.Closed),
      )
    );
  }

  public isEditButtonHidden(selectedItems: ISiteInventoryCount[]): boolean {
    return get(selectedItems, 'length') > 1 || this.isCompletedCountGroup();
  }

  public isSubmitButtonHidden(selectedItems: ISiteInventoryCount[]): boolean {
    return (
      isEmpty(selectedItems) ||
      some(
        selectedItems,
        (item) =>
          item?.status !== INVENTORY_COUNT_STATUS_VALUE.Open ||
          isEmpty(item.inventoryCountId),
      )
    );
  }

  public isViewButtonHidden(selectedItems: ISiteInventoryCount[]): boolean {
    return get(selectedItems, 'length') > 1 || !this.isCompletedCountGroup();
  }

  public isCategoryExportHidden(viewModal: GRID_LIST_VIEW_TYPE): boolean {
    const viewSetting = this.getFormFieldValue(
      this.formFieldPath.virtualViewSetting,
    );

    return !isEqual(viewSetting, viewModal);
  }

  public async saveInventoryCountGroup(
    document: IInventoryCountGroup,
  ): Promise<any> {
    const options: IDocumentOperationOptions = {
      suppressSuccessMessage: true,
      suppressErrors: true,
    };

    if (this.isNewMode()) {
      return this.apiActions.createEntityAsPromised(
        ENTITY_TYPES.INVENTORY_COUNT_GROUP,
        document,
        {
          ...options,
          mode: this.mode,
        },
      );
    }

    const docForUpdate = {};

    // In case of update action the only name and site_ids fields can be updated
    const nameControl: AbstractControl = this.getFormFieldControl(
      this.formFieldPath.name,
    );
    const sitesControl: AbstractControl = this.getFormFieldControl(
      this.formFieldPath.virtualSiteInventoryCounts,
    );

    if (nameControl?.dirty) {
      set(docForUpdate, this.formFieldPath.name, nameControl.value);
    }

    if (sitesControl?.dirty) {
      set(
        docForUpdate,
        this.formFieldPath.virtualSiteInventoryCounts,
        sitesControl.value,
      );
    }

    return this.apiActions.updateEntityAsPromised(
      ENTITY_TYPES.INVENTORY_COUNT_GROUP,
      docForUpdate,
      {
        ...options,
        endpoint: this.document?._id,
      },
    );
  }

  public async updateSiteInventoryCounts(
    inventoryCountGroupEntityId: string,
  ): Promise<any[]> {
    if (!inventoryCountGroupEntityId) return;
    const currentDocSiteIds: string[] = get(this.document, 'site_ids', []);
    const currentInventoryCounts = this.getFormFieldValue<IInventoryCount>(
      this.formFieldPath.virtualSiteInventoryCounts,
    );
    const inventoryCountName = this.getFormFieldValue<string>(
      this.formFieldPath.name,
    );
    const notExistedInventoryCounts = filter(
      currentInventoryCounts,
      (count) =>
        isEmpty(count.inventoryCountId) &&
        !includes(currentDocSiteIds, count.id),
    );
    if (isEmpty(notExistedInventoryCounts)) return;
    const preparedCountsToCreate = map(notExistedInventoryCounts, (count) => {
      return {
        name: inventoryCountName,
        status: INVENTORY_COUNT_STATUS_VALUE.Open,
        start_date: toMoment().toISOString(),
        date: toMoment().toISOString(),
        inventory_count_items: [],
        inventory_count_group_entity_id: inventoryCountGroupEntityId,
        site_id: count.id,
        counted_value: 0,
        variance_total: 0,
        entity_id: generateObjectId(),
      };
    });

    return this.inventoryCountGroupService.createInventoryCounts(
      preparedCountsToCreate,
    );
  }

  public editButtonClick(value: string, gridItem: ISiteInventoryCount): void {
    this.openSiteCount([gridItem]);
  }

  public composeConfirmationModalOption(
    action,
    selectedItems,
  ): IModalDialogOptions {
    if (action === BUTTON_ACTIONS.REMOVE) {
      const confirmationObj = eq(size(selectedItems), 1)
        ? {
            message: I18n.ConfirmMessages.AreYouSureYouWantToRemoveSiteCount,
            items: get(selectedItems[0], 'name', size(selectedItems)),
          }
        : {
            message:
              I18n.ConfirmMessages.AreYouSureYouWantToRemoveSelectedSiteCounts,
            items: size(selectedItems),
          };

      return {
        text: this.i18nService.instant(confirmationObj.message, {
          removableItems: confirmationObj.items,
        }),
      };
    }
  }

  public getSiteSelectorButtonCaption(
    selectedModalSites: IInventoryCountSelectedModalSites,
  ): string {
    const itemsLength = selectedModalSites.items.length;

    return itemsLength > 1
      ? this.i18nService.instant(I18n.Common.SelectXEntities, {
          count: itemsLength,
          entities: this.i18nService.instant(
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        })
      : this.i18nService.instant(I18n.Common.SelectSitesWithBracket);
  }

  public async updateSiteCount(
    input: IBaseEntityType,
    options: IDocumentOperationOptions,
  ): Promise<any> {
    if (isEmpty(input)) return {};

    return this.apiActions.updateEntityAsPromised(
      ENTITY_TYPES.INVENTORY_COUNT,
      input,
      {
        ...options,
      },
    );
  }

  public numberValueFormatter(params: ValueFormatterParams): string | null {
    return !isUndefined(params?.value)
      ? this.numberFormatService.getNumberFormatter(true)(params)
      : null;
  }

  private setSubtitleForModel(grid: XuxGridListComponent): void {
    this.subscriptions.push(
      grid.visibleItemCount$.subscribe((totalCountVisible: number) => {
        this.siteModalTotal = totalCountVisible;
        this.siteModalDescription$.next(
          this.getSitesListText(
            this.siteModalSelected,
            this.siteModalTotal,
            I18n.EntityNames.SiteSingularAndPlural,
          ),
        );
      }),
    );
  }

  private stringSortable(): any {
    return (valueA, valueB): any => {
      if (valueA.toString().toLowerCase() === valueB.toString().toLowerCase()) {
        return 0;
      }

      return valueA.toString().toLowerCase() > valueB.toString().toLowerCase()
        ? 1
        : -1;
    };
  }

  private getInventoryCountWithSiteData(
    ids: string[],
  ): Observable<IApiListResponse> {
    const itemFieldsSelect = [
      'name',
      'entity_id',
      'site_id',
      'reporting_category_major',
      'reporting_category_minor',
      'counts',
    ];

    return this.fetchInventoryCounts({
      filter: `inventory_count_group_entity_id eq in(${this.document?.entity_id || ''}) and site_id eq in(${ids.join(
        ',',
      )})`,
      select: ['site_id', 'status', 'inventory_count_items'],
      include_items: true,
      item_fields_select: itemFieldsSelect.join(),
    });
  }

  private updateXVICountsOnDMApiData(
    dmData: SiteDataForPdf,
    xviData: SiteDataForPdf,
  ): SiteDataForPdf {
    if (isEmpty(dmData)) {
      return null;
    }

    const dataList: SiteDataCommonRow[] = [];
    dmData.items.forEach((dmItem) => {
      const currentXvi = find(xviData.items, {
        entity_id: dmItem.entity_id,
        site_id: dmItem.site_id,
      });
      if (!isEmpty(currentXvi)) {
        dataList.push({ ...dmItem, measure: currentXvi.measure });
      } else {
        dataList.push(dmItem);
      }
    });

    const returnObj: SiteDataForPdf = {
      extraProp: { ...dmData, ...xviData },
      items: dataList,
    };

    return returnObj;
  }

  private formatSiteDataForExport(
    data: ExportData[],
    withCounts: boolean,
    extraProp: ExtraProp,
  ): SiteDataForPdf {
    const dataList: SiteDataCommonRow[] = [];
    const sortedItems: ExportData[] = sortBy(
      map(data, (item) => ({
        ...item,
        sortIdentity: toLower(
          `${get(item, COMMON_FORM_PATH.reportingCategoryMajorName, '')}_${get(
            item,
            COMMON_FORM_PATH.reportingCategoryMinorName,
            '',
          )}_${get(item, 'name', '')}`,
        ),
      })),
      'sortIdentity',
    );

    forEach(sortedItems, (obj) => {
      let tempData: SiteDataCommonRow = null;
      if (withCounts) {
        tempData = {
          name: obj.name,
          site_id: obj.site_id,
          site_name: obj.reporting_category_major.name,
          major_category_name: obj.reporting_category_major.name,
          minor_category_name: obj.reporting_category_minor.name,
          reporting_category_major: {
            name: obj.reporting_category_major.name,
          },
          reporting_category_minor: {
            name: obj.reporting_category_minor.name,
          },
          measure: obj.counts.map((x) => ({
            name: x.measure_description,
            measure_description: x.measure_description,
            factor_value: x.factor_value,
            count: x.count,
          })),
          isHaveCounts: true,
          entity_id: obj.entity_id,
        };
      } else {
        const measureItems = InventoryCountGroupService.isCompanyLevel
          ? filter(
              get(obj, 'measures', []),
              (measure) => measure?.transactions?.physical_count === true,
            )
          : get(obj, 'physical_count_measures', []);
        tempData = {
          name: obj.name,
          site_id: obj.site_id,
          site_name: obj.reporting_category_major.name,
          major_category_name: obj.reporting_category_major.name,
          minor_category_name: obj.reporting_category_minor.name,
          reporting_category_major: {
            name: obj.reporting_category_major.name,
          },
          reporting_category_minor: {
            name: obj.reporting_category_minor.name,
          },
          measure: measureItems.map((x) => ({
            name: x.description,
            measure_description: x.description,
            factor_value: x.factor_value,
            count: null,
          })),
          isHaveCounts: false,
          entity_id: obj.entity_id,
        };
      }
      dataList.push(tempData);
    });
    const returnObj: SiteDataForPdf = {
      extraProp,
      items: dataList,
    };

    return returnObj;
  }

  private setControlValue(control: AbstractControl, value): void {
    control.setValue(value);
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  private prepareSiteCountsGridItem(
    site,
    currentCount,
  ): IInventoryCountFormFieldPath {
    return {
      ...site,
      status: get(currentCount, 'status', INVENTORY_COUNT_STATUS_VALUE.Open),
      inventoryCountId: get(currentCount, '_id', ''),
      inventoryCounts: get(currentCount, 'virtual.inventory_count_items', []),
      variance_cost: get(currentCount, 'variance_cost', 0),
    };
  }
  // eslint-disable-next-line max-lines
}
